import React from 'react';
import { API } from '../api';
import { Section } from './ui';

/*
 * What the club's posters keep having in common.
 *
 * This replaced a text box holding a description the app rewrote on every
 * approved poster. The rewrite forgot: one flat-graphic quiz night poster
 * permanently erased "golden hour" and "never corporate stock imagery" from
 * a club whose every other poster had both, and the description still grew
 * from 230 characters to over 1,100.
 *
 * So the club's look is now kept as traits with a count of how often each has
 * been seen, and the description handed to the next poster is written from
 * the ones that recur. Seen once, a trait is noted and ignored. Seen twice,
 * it is the club's style. That is what the counts on this screen mean, and
 * why a one-off experiment does not redefine anything.
 *
 * Pinning forces a trait in regardless of its count; removing takes one out
 * for good. Between them they are how an admin corrects the club's look —
 * and unlike the old text box, a correction here survives the next poster.
 */

function StyleTraits({ clubId, showToast }) {
  const [note, setNote] = React.useState('');
  const [traits, setTraits] = React.useState(null);
  const qs = clubId ? '?clubId=' + clubId : '';

  const load = React.useCallback(async () => {
    const r = await fetch(`${API}/api/posters/style${qs}`);
    if (!r.ok) { setTraits([]); return; }
    const data = await r.json();
    setNote(data.note || '');
    setTraits(data.traits || []);
  }, [qs]);

  React.useEffect(() => { load(); }, [load]);

  const change = async (trait, patch) => {
    const r = await fetch(`${API}/api/posters/style/${trait.id}${qs}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clubId, ...patch }),
    });
    if (!r.ok) return showToast('That did not save', 'red');
    const data = await r.json();
    setNote(data.note || '');
    setTraits(data.traits || []);
    return undefined;
  };

  if (!traits) {
    return (
      <Section label="What it has learned so far">
        <p className="w3-text-grey">Loading…</p>
      </Section>
    );
  }

  const inNote = traits.filter(t => t.inNote);
  const noticed = traits.filter(t => !t.inNote);

  return (
    <Section label="What it has learned so far">
      <p className="style-intro">
        Every poster you put on the club screen is looked at for what it has in common with the
        ones before it. Anything seen twice becomes part of your club’s look and goes into every
        poster made afterwards; anything seen once is noted and left alone, so a one-off does not
        change how everything else turns out.
      </p>

      {traits.length === 0 ? (
        <p className="w3-text-grey">
          Nothing yet. Put a poster on the club screen and this starts building itself.
        </p>
      ) : (
        <>
          <div className="style-note">
            <span className="gs-section-label">Used on every poster</span>
            <p>{note || 'Nothing is established yet — one more poster will do it.'}</p>
          </div>

          <div className="style-list">
            {inNote.map(t => (
              <div key={t.id} className="style-trait is-on">
                <span className="style-count" title={`Seen ${t.count} times`}>{t.count}×</span>
                <span className="style-text">{t.text}</span>
                {t.pinned && <span className="style-pin">pinned</span>}
                <button className="w3-button w3-small w3-border"
                  onClick={() => change(t, { pinned: !t.pinned })}>
                  {t.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button className="w3-button w3-small w3-border"
                  title="Take this out of your club's look for good"
                  onClick={() => change(t, { removed: true })}>
                  <i className="fa fa-trash"></i>
                </button>
              </div>
            ))}
          </div>

          {noticed.length > 0 && (
            <>
              <span className="gs-section-label style-heading">Noticed once, not used yet</span>
              <div className="style-list">
                {noticed.map(t => (
                  <div key={t.id} className="style-trait">
                    <span className="style-count">{t.count}×</span>
                    <span className="style-text">{t.text}</span>
                    <button className="w3-button w3-small w3-border"
                      title="Use this from now on, however rarely it appears"
                      onClick={() => change(t, { pinned: true })}>
                      Pin
                    </button>
                    <button className="w3-button w3-small w3-border"
                      onClick={() => change(t, { removed: true })}>
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Section>
  );
}

export default StyleTraits;
